import { Button } from '@/components/ui/button';
import { LoadingBadge, LoadingBadgeKeyframes } from '@/components/ui/LoadingBadge';
import { execute } from '@/graphql/execute';
import { offlineCatalogAtom } from '@/lib/offline/catalog';
import {
  downloadFiles,
  QuotaExceededAbort,
  type ManifestFile,
} from '@/lib/offline/downloadManager';
import { createOfflineStorage } from '@/lib/offline/createOfflineStorage';
import { isTauri } from '@/lib/offline/isTauri';
import { tauriDownloadFiles } from '@/lib/offline/tauriDownload';
import { putSnapshot, snapshotKey } from '@/lib/offline/snapshotStore';
import { resourceKey } from '@/lib/offline/resourceKey';
import { loadManifest as defaultLoadManifest } from '@/lib/offline/useLoadManifest';
import { useOfflineEnabled } from '@/lib/offline/useOfflineEnabled';
import { featuresByProjectQuery } from '@/queries/feature.query';
import { fetchImageLayers } from '@/lib/images/imageLayerPack';
import { useAtom } from 'jotai';
import { CloudDownload, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface Props {
  projectId: string;
  loadManifest?: (projectId: string) => Promise<{
    files: ManifestFile[];
    totalBytes: number;
    layerVersions: Record<string, string>;
  }>;
}

export function MakeAvailableOfflineButton({ projectId, loadManifest = defaultLoadManifest }: Props) {
  const { t } = useTranslation('project');
  const offlineEnabled = useOfflineEnabled();
  const [catalog, setCatalog] = useAtom(offlineCatalogAtom);
  const [busy, setBusy] = useState(false);
  const [bytesDone, setBytesDone] = useState(0);
  const [bytesTotal, setBytesTotal] = useState(0);

  const entry = catalog[projectId];

  const download = useCallback(async () => {
    setBusy(true);
    try {
      const storage = await createOfflineStorage();
      const manifest = await loadManifest(projectId);

      // eslint-disable-next-line no-console
      console.info('[offline] manifest', {
        files: manifest.files.length,
        totalBytes: manifest.totalBytes,
        sampleUrls: manifest.files.slice(0, 3).map((f) => f.url),
      });

      const usage = await storage.usage();
      if (!entry && usage.quota != null && manifest.totalBytes > usage.quota - usage.used) {
        setCatalog((c) => ({
          ...c,
          [projectId]: {
            projectId,
            status: 'error',
            totalBytes: manifest.totalBytes,
            downloadedBytes: 0,
            layerVersions: {},
            downloadedAt: new Date().toISOString(),
            error: t('offline.errorTooLarge'),
          },
        }));
        return;
      }

      setBytesTotal(manifest.totalBytes);
      setBytesDone(0);
      setCatalog((c) => ({
        ...c,
        [projectId]: {
          projectId,
          status: 'downloading',
          totalBytes: manifest.totalBytes,
          downloadedBytes: 0,
          layerVersions: manifest.layerVersions,
          downloadedAt: new Date().toISOString(),
        },
      }));

      const reportBytes = (bytes: number) => {
        setBytesDone(bytes);
        setCatalog((c) => ({
          ...c,
          [projectId]: { ...c[projectId], downloadedBytes: bytes },
        }));
      };

      if (isTauri()) {
        // Tauri: the WKWebView can't fetch CloudFront directly (CORS + cookies),
        // so the Rust `download_files` command streams each file to disk through
        // the shared cookie jar, reporting live cumulative bytes for the bar.
        await tauriDownloadFiles(
          manifest.files.map((f) => ({ key: resourceKey(f.url), url: f.url })),
          reportBytes,
        );
      } else {
        await downloadFiles(manifest.files, { storage, onProgress: (_files, bytes) => reportBytes(bytes) });
      }

      // Snapshot all project features so vector layers render offline. This is
      // best-effort: if it fails the download still completes as `ready`, the
      // vector features simply won't be available without a connection.
      try {
        const featureData = await execute(featuresByProjectQuery, { projectId });
        await putSnapshot(storage, projectId, 'features', featureData.features);
      } catch (snapErr) {
        console.warn('Offline feature snapshot failed; vector layers will not render offline', snapErr);
      }

      // Snapshot image-layer POIs so markers render offline even if the project
      // was never opened in the viewer online. Best-effort, same as features.
      try {
        const imageLayerIds = [...new Set(manifest.files.filter((f) => f.kind === 'IMAGE').map((f) => f.layerId))];
        if (imageLayerIds.length > 0) {
          await putSnapshot(storage, projectId, 'images', await fetchImageLayers(imageLayerIds));
        }
      } catch (imgErr) {
        console.warn('Offline image snapshot failed; image POIs will not render offline', imgErr);
      }

      const fileKeys = manifest.files.map((f) => resourceKey(f.url));
      setCatalog((c) => ({
        ...c,
        [projectId]: { ...c[projectId], status: 'ready', downloadedAt: new Date().toISOString(), fileKeys },
      }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[offline] download failed', e);
      const partial = e instanceof QuotaExceededAbort;
      const detail = e instanceof Error && e.message ? e.message : '';
      const message = partial
        ? t('offline.errorOutOfSpace')
        : detail
          ? `${t('offline.errorGeneric')} (${detail})`
          : t('offline.errorGeneric');
      setCatalog((c) => ({
        ...c,
        [projectId]: { ...c[projectId], status: partial ? 'partial' : 'error', error: message },
      }));
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }, [projectId, loadManifest, setCatalog, t]);

  // Warn before reload/app-close mid-download so the offline copy isn't left
  // half-written. (In-app SPA navigation doesn't interrupt the download — the
  // Rust command / async task keeps running and the sidebar indicator keeps
  // showing the `downloading` state — so no router-level blocking is needed,
  // which also avoids `useBlocker` (data-router only) crashing the classic
  // BrowserRouter.)
  useEffect(() => {
    if (!busy) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [busy]);

  const remove = useCallback(async () => {
    const storage = await createOfflineStorage();
    const keys = catalog[projectId]?.fileKeys ?? [];
    for (const key of keys) {
      await storage.delete(key);
    }
    // Also remove the project's vector-feature snapshot.
    await storage.delete(snapshotKey(projectId, 'features'));
    setCatalog((c) => {
      const next = { ...c };
      delete next[projectId];
      return next;
    });
  }, [projectId, catalog, setCatalog]);

  const isReady = entry?.status === 'ready';

  // PostHog-gated: hide the entry point entirely when the flag is off.
  if (!offlineEnabled) return null;

  return (
    <div className='space-y-2'>
      <LoadingBadgeKeyframes />
      {isReady ? (
        <Button type='button' variant='outline' onClick={remove} className='w-full'>
          <Trash2 />
          {t('offline.removeOfflineCopy')}
        </Button>
      ) : (
        <Button type='button' variant='outline' onClick={download} disabled={busy} className='w-full'>
          <CloudDownload />
          {t('offline.makeAvailableOffline')}
        </Button>
      )}
      {busy && (
        <LoadingBadge
          name={t('offline.downloading')}
          phase='fetching'
          progress={bytesTotal ? bytesDone / bytesTotal : undefined}
          loadedBytes={bytesDone}
          totalBytes={bytesTotal}
          labels={{
            fetching: t('offline.downloading'),
            decoding: t('offline.downloading'),
            failed: t('offline.errorGeneric'),
          }}
        />
      )}
      {!busy && entry && (entry.status === 'error' || entry.status === 'partial') && entry.error && (
        <p className='text-xs text-red-600 dark:text-red-400'>{entry.error}</p>
      )}
    </div>
  );
}
